<script>
import ContractRestructuringMenu from './ContractRestructuringMenu.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import BlockContent from '../UI/Content/BlockContent.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'
import mixinComponent from '../../Mixins/mixinComponent'
import { globalVariablesMixin } from '../../Mixins/global'
import ContractRestructuringFormContractStep from './Form/ContractRestructuringFormContractStep.vue'
import ContractRestructuringFormOutstandingRepurchaseStep from './Form/ContractRestructuringFormOutstandingRepurchaseStep.vue'
import { Routes } from '../../../../Constants/Routes.ts'
import { ContractRestructuringActionType } from '../../../../Constants/ContractRestructuringActionType.ts'
import ModalConfirmation from '../UI/Modal/ModalConfirmation.vue'

export default {
  components: {
    ModalConfirmation,
    BlockContent,
    ButtonAll,
    ContractRestructuringFormContractStep,
    ContractRestructuringFormOutstandingRepurchaseStep,
    ContractRestructuringMenu,
    FieldCreator,
    FormCreator,
    LazyContent,
  },
  mixins: [mixinComponent, globalVariablesMixin],
  data: (vm) => ({
    params: {
      types: [],
    },
    loadField: false,
    loading: true,
    sending: false,
    form: null,
    step: vm.$route.meta.step ?? 'contracts',
    selectedContractIds: [],
    restructuring: null,
    canceling: false,
  }),
  computed: {
    restructuringId() {
      return this.$route.params.id ?? this.restructuring?.id
    },
    menuItems() {
      const menu = [
        {
          menu: 'contracts',
          label: this.$t('restructuring.steps.contract_selection'),
          enabled: !this.loading && this.step === 'configuration',
          index: 1,
          submit: false,
          previous: false,
          next: true,
        },
        {
          menu: 'configuration',
          label: this.$t('restructuring.steps.configuration'),
          enabled: this.step === 'configuration',
          index: 2,
          submit: true,
          previous: true,
          next: true,
        },
      ]

      if (this.hasOutstandingRepurchaseAction) {
        menu.push({
          menu: 'outstanding_repurchase',
          label: this.$t('contract.restructuring.types.outstanding_repurchase.label'),
          enabled: this.step === 'outstanding_repurchase',
          index: menu.length + 1,
          submit: false,
          previous: false,
          next: true,
          cancelable: true,
        })
      }

      return menu
    },
    currentMenuItem() {
      return this.menuItems.find(({ menu }) => this.step === menu)
    },
    nextMenuItem() {
      const currentMenuItemIndex = this.menuItems.indexOf(this.currentMenuItem)

      return this.menuItems[currentMenuItemIndex + 1] ?? null
    },
    previousMenuItem() {
      if (this.currentMenuItem.previous === false) {
        return null
      }

      const currentMenuItemIndex = this.menuItems.indexOf(this.currentMenuItem)

      return this.menuItems[currentMenuItemIndex - 1] ?? null
    },
    hasOutstandingRepurchaseAction() {
      return this.params.types.includes(ContractRestructuringActionType.OUTSTANDING_REPURCHASE)
    },
    ready() {
      return !!this.restructuring
    },
  },
  methods: {
    setLoading(loading) {
      this.loading = loading
    },
    setForm(form) {
      this.form = form
    },
    onTypesChange(types) {
      this.params.types = types
    },
    async submit() {
      return await this.$refs.formrestructuration.methods(this.restructuring?.id ? 'patch' : 'post')
    },
    redirectAfterSubmit() {
      // It should always be that type as it's the only one for now but not all actions will redirect to simulation
      if (this.hasOutstandingRepurchaseAction) {
        window.location.href = `/devis/simulation.php?index=creer&restructuringId=${this.restructuring.id}`
        return
      }

      this.$router.push({ name: Routes.RESTRUCTURING_LIST })
    },
    setStep(step) {
      this.step = step
    },
    async goToNextStep() {
      const { submit } = this.currentMenuItem
      if (submit) {
        this.sending = true
        const succeed = await this.submit()
        if (!succeed) {
          this.sending = false
          return
        }

        await this.$refs.formrestructuration.get()
        this.sending = false
        this.$router.push({ name: Routes.RESTRUCTURING_EDIT, params: { id: this.restructuringId } })
      }

      await this.$nextTick()
      if (!this.nextMenuItem) {
        this.redirectAfterSubmit()
      } else {
        this.setStep(this.nextMenuItem.menu)
      }
    },
    goToPreviousStep() {
      this.setStep(this.previousMenuItem.menu)
    },
    setRestructuring(restructuring) {
      this.restructuring = restructuring
      this.params.types = restructuring.types

      if (this.params.types.length === 0 && Object.keys(this.form.def.types.options.choices).length === 1) {
        this.params.types.push(Object.values(this.form.def.types.options.choices)[0])
      }
    },
    askCancelationConfirmation() {
      this.$refs.cancelationConfirmModal.showModal()
    },
    async cancelRestructuring() {
      this.$refs.cancelationConfirmModal.closeModal()
      this.canceling = true
      await this.postJSON(`contracts/restructuring/${this.restructuring.id}/cancel`)
      this.canceling = false

      this.$router.push({ name: Routes.RESTRUCTURING_VIEW, params: { id: this.restructuring.id } })
    },
  },
  watch: {
    selectedContractIds: {
      handler(values) {
        this.$refs.formrestructuration.datas['contracts'] = values
      },
      deep: true,
    },
  },
}
</script>
<template>
  <div>
    <ContractRestructuringMenu
      :title="$t('restructuring.title.create')"
      :items="menuItems"
      :sticky="false"
      v-model:active-menu="step"
    />

    <FormCreator
      endpoint="contracts/restructuring"
      :id="restructuringId"
      :params="restructuringId ? null : params"
      ref="formrestructuration"
      src-error="formrestructuration"
      class-to-add="d-none"
      @object-build="setRestructuring"
      @form-return="setRestructuring"
      @update-data="setForm"
      @is-loading="setLoading"
    />

    <LazyContent :loading="loading" :class="{ 'mt-4': loading }">
      <template v-if="ready">
        <div>
          <ContractRestructuringFormContractStep
            :class="{ 'd-none': step !== 'contracts' }"
            v-model:contract-ids="selectedContractIds"
            @next="goToNextStep"
          />
          <BlockContent :class="{ 'd-none': step !== 'configuration', 'mt-2': true, 'px-0': true }">
            <div class="row">
              <div class="col-md-6">
                <FieldCreator
                  name="label"
                  form-ref="formrestructuration"
                  domain="restructuring"
                  :form-data="form"
                  label-top
                />
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <FieldCreator
                  name="types"
                  form-ref="formrestructuration"
                  domain="restructuring"
                  :form-data="form"
                  label-top
                  multiple
                  select-multiple
                  type-value="string"
                  @get-select-change="onTypesChange"
                />
              </div>
            </div>
          </BlockContent>
          <ContractRestructuringFormOutstandingRepurchaseStep
            v-if="step === 'outstanding_repurchase'"
            :restructuring="restructuring"
            :form="form"
          />
        </div>
      </template>
    </LazyContent>

    <div
      v-if="ready"
      class="d-flex mt-2"
      :class="{ 'justify-content-end': !previousMenuItem, 'justify-content-between': previousMenuItem }"
    >
      <ButtonAll
        v-if="previousMenuItem"
        margin="no"
        label="restructuring.actions.previous"
        type="validation"
        :disabled="!currentMenuItem?.previous"
        @click="goToPreviousStep"
      />

      <div>
        <ButtonAll
          v-if="currentMenuItem.cancelable"
          margin="no"
          class="mr-2"
          label="restructuring.actions.cancel"
          type="warning"
          @click="askCancelationConfirmation"
        />

        <ButtonAll
          margin="no"
          :label="nextMenuItem ? 'restructuring.actions.next' : 'restructuring.actions.submit'"
          type="validation"
          :loading="sending"
          :disabled="!currentMenuItem?.next"
          @click="goToNextStep"
        />
      </div>
    </div>
  </div>

  <ModalConfirmation
    ref="cancelationConfirmModal"
    title="restructuring.actions.confirmCancelation"
    button-confirm-title="main.bool.true"
    button-cancel-title="main.bool.false"
    :loader="canceling"
    @confirm="cancelRestructuring"
  />
</template>
